import { throttle } from 'lodash';
import { useEffect, useState } from 'react';
import { ScreenSize, breakpointUpperLimit } from './device-breakpoints';

const getSingleBreakpoint = (
  width: number,
  screenSize: string,
  upperLimits: { [key: string]: number }
): boolean => {
  return width <= upperLimits[screenSize];
};

const getBreakpoints = (
  width: number,
  upperLimits: { [key: string]: number }
): { [key in string]: boolean } => {
  const breakpointStatuses = { mobile: false, tablet: false };
  Object.keys(upperLimits).forEach((screenSize: string) => {
    breakpointStatuses[screenSize] = getSingleBreakpoint(
      width,
      screenSize,
      upperLimits
    );
  });
  return breakpointStatuses;
};

const useBreakpointGeneric = (upperLimits: { [key: string]: number }) => {
  const breakpointStates = {};
  Object.keys(upperLimits).forEach((screenSize: string) => {
    const [state, setState] = useState<boolean>(() =>
      getSingleBreakpoint(window.innerWidth, screenSize, upperLimits)
    );
    breakpointStates[screenSize] = [state, setState];
  });

  useEffect(() => {
    const recalculateBreakpoints = throttle(() => {
      const newBreakpointStates = getBreakpoints(
        window.innerWidth,
        upperLimits
      );
      Object.keys(upperLimits).forEach(screenSize => {
        const [state, setState] = breakpointStates[screenSize];
        setState(newBreakpointStates[screenSize]);
      });
    }, 200);

    window.addEventListener('resize', recalculateBreakpoints);
    return () => window.removeEventListener('resize', recalculateBreakpoints);
  }, []);

  const justState = Object.keys(upperLimits).reduce((map, key) => {
    map[key] = false;
    return map;
  }, {} as { [key in string]: boolean });

  Object.keys(upperLimits).forEach(screenSize => {
    const [state, setState] = breakpointStates[screenSize];
    justState[screenSize] = state;
  });
  return justState;
};

const useBreakpoint = () => {
  return useBreakpointGeneric(breakpointUpperLimit) as {
    [key in ScreenSize]: boolean;
  };
};

export const useCustomBreakpoint = (upperLimitPx: number) => {
  const key = 'cutomBreakpoint';
  const response = useBreakpointGeneric({ [key]: upperLimitPx });
  return response[key];
};

export default useBreakpoint;
